import { combineReducers, createReducer, PayloadAction } from "@reduxjs/toolkit";

import { get, STORAGE_KEYS } from "API/LocalStorage";
import { ThemeType } from "Model/Configuration/ITheme";
import { IMeta } from "Model/State/INotificationMeta";
import { IUiState } from "Model/State/IUiState";
import { IProject } from "Model/Storage/IProject";
import {
  addressValidationError,
  addressValidationStart,
  addressValidationSuccess,
  duplicateCheckError,
  duplicateCheckStart,
  duplicateCheckSuccess,
  importError,
  importStart,
  importSuccess,
  projectDeleteSuccess,
  projectsClear,
  projectsFilterIdListSet,
  projectsFilterStart,
  projectsRetrieveError,
  projectsRetrieveSinceError,
  projectsRetrieveSinceStart,
  projectsRetrieveSinceSuccess,
  projectsRetrieveStart,
  projectsRetrieveSuccess,
  projectsSortStart,
  projectsSortSuccess,
  projectsStagingManageError,
  projectsStagingManageStart,
  projectsStagingManageSuccess,
  projectsStagingUpdateError,
  projectsStagingUpdateStart,
  projectsStagingUpdateSuccess,
  projectsUpdateError,
  projectsUpdateStart,
  projectsUpdateSuccess,
  uiLastOpenedPIDSet,
  uiLoginUsernameSet,
  uiRefreshEveryTimeSet,
  uiSetActiveCompanyId,
  uiSetActiveContactId,
  uiSetActiveProjectId,
  uiSetConfigurationReady,
  uiSetDetailsOpen,
  uiSetIsFiltering,
  uiSetIsSorting,
  uiSetSelectedProjectIds,
  uiSetTheme,
  uiSetToolbarIconsOnly,
  uiSetTranslationReady,
  uiSetUserSettingsOpen,
  uiStagingOpenSet,
  versionsGetSuccess
} from "Store/Actions";
import { removeElement } from "Utils/Array";
import { isNullOrWhiteSpace } from "Utils/String";
import { AutoCompleteSuggestions } from "./AutoCompleteSuggestions";
import { Language } from "./Language";
import { Notifications } from "./Notifications";
import { Usernames } from "./Usernames";

const activeProjectId = createReducer<string>(null, {
  [uiSetActiveProjectId.type]: (_, { payload }) => payload,
  [uiSetDetailsOpen.type]: (state, { payload }) => (payload === false ? null : state),
  [projectsClear.type]: () => null
});

const activeCompanyId = createReducer<string>(null, {
  [uiSetActiveCompanyId.type]: (_, { payload }) => payload,
  [uiSetDetailsOpen.type]: (state, { payload }) => (payload === false ? null : state),
  [projectsClear.type]: () => null
});

const activeContactId = createReducer<string>(null, {
  [uiSetActiveContactId.type]: (_, { payload }) => payload,
  [uiSetDetailsOpen.type]: (state, { payload }) => (payload === false ? null : state),
  [projectsClear.type]: () => null
});

const selectedIds = createReducer<string[]>([], {
  [uiSetSelectedProjectIds.type]: (_, { payload }: PayloadAction<string[]>) => [...payload],
  [projectDeleteSuccess.type]: (state, { payload }: PayloadAction<string>) => removeElement(state, payload),
  [projectsClear.type]: () => []
});

const isDetailsOpen = createReducer(false, {
  [uiSetDetailsOpen.type]: (_, { payload }) => payload,
  [projectsClear.type]: () => false
});

const isToolbarIconsOnly = createReducer(get<boolean>(STORAGE_KEYS.TOOLBAR_ONLY_ICONS), {
  [uiSetToolbarIconsOnly.type]: (_, { payload }) => payload
});

const isUpdateOpen = createReducer(false, {
  [uiStagingOpenSet.type]: (_, { payload }) => payload,
  [projectsStagingUpdateSuccess.type]: () => true
});

const isUserSettingsOpen = createReducer(false, {
  [uiSetUserSettingsOpen.type]: (_, { payload }) => payload
});

const isLoading = createReducer<boolean>(false, {
  [addressValidationError.type]: () => false,
  [addressValidationStart.type]: () => true,
  [addressValidationSuccess.type]: () => false,
  [duplicateCheckError.type]: () => false,
  [duplicateCheckStart.type]: () => true,
  [duplicateCheckSuccess.type]: () => false,
  [projectsRetrieveError.type]: () => false,
  [projectsRetrieveStart.type]: () => true,
  [projectsRetrieveSuccess.type]: () => false,
  [projectsRetrieveSuccess.type]: () => false,
  [projectsStagingManageError.type]: () => false,
  [projectsStagingManageStart.type]: () => true,
  [projectsStagingManageSuccess.type]: () => false,
  [projectsStagingUpdateError.type]: () => false,
  [projectsStagingUpdateStart.type]: () => true,
  [projectsStagingUpdateSuccess.type]: () => false,
  [projectsUpdateError.type]: () => false,
  [projectsUpdateStart.type]: (_, action: PayloadAction<IProject[], "projects/update/start", IMeta>) => action.meta.showNotification,
  [projectsUpdateSuccess.type]: () => false,
  [uiSetDetailsOpen.type]: (state, action: PayloadAction<boolean>) => (action?.payload === true ? false : state)
});

const isImporting = createReducer<boolean>(false, {
  [importError.type]: () => false,
  [importStart.type]: () => true,
  [importSuccess.type]: () => false
});

const isAutoRefresh = createReducer<boolean>(false, {
  [projectsRetrieveSinceStart.type]: () => true,
  [projectsRetrieveSinceError.type]: () => false,
  [projectsRetrieveSinceSuccess.type]: () => false
});

const isFiltering = createReducer<boolean>(false, {
  [projectsFilterIdListSet.type]: () => false,
  [projectsFilterStart.type]: () => true,
  [uiSetIsFiltering.type]: () => true
});

const isSorting = createReducer<boolean>(false, {
  [projectsSortSuccess.type]: () => false,
  [projectsSortStart.type]: () => true,
  [uiSetIsSorting.type]: () => true
});

const isConfigurationReady = createReducer(false, {
  [uiSetConfigurationReady.type]: () => true
});

const isTranslationReady = createReducer(false, {
  [uiSetTranslationReady.type]: (_, { payload }) => payload ?? false
});

const loginUsername = createReducer(null, {
  [uiLoginUsernameSet.type]: (_, { payload: username }) => {
    if (isNullOrWhiteSpace(username)) {
      return null;
    }

    return username;
  }
});

const versions = createReducer(
  {},
  {
    [versionsGetSuccess.type]: (state, { payload }) => (payload != null ? payload : state)
  }
);
const lastOpenedPID = createReducer(null, {
  [uiLastOpenedPIDSet.type]: (_, { payload }) => payload
});

const refreshEvery = createReducer(60, {
  [uiRefreshEveryTimeSet.type]: (_, { payload }) => payload
});

const theme = createReducer(ThemeType.Auto, {
  [uiSetTheme.type]: (_, { payload }) => payload
});

export const UI = combineReducers<IUiState>({
  activeCompanyId,
  activeContactId,
  activeProjectId,
  autoCompleteSuggestions: AutoCompleteSuggestions,
  isAutoRefresh,
  isConfigurationReady,
  isDetailsOpen,
  isFiltering,
  isImporting,
  isLoading,
  isSorting,
  isStagingOpen: isUpdateOpen,
  isToolbarIconsOnly,
  isTranslationReady,
  isUserSettingsOpen,
  language: Language,
  lastOpenedPID,
  loginUsername,
  notification: Notifications,
  refreshEvery,
  selectedIds,
  theme,
  usernames: Usernames,
  versions
});
